import axios from "axios";
import { getSignupSource } from "./signupSource";

export const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;

// A request that never answers must eventually fail visibly instead of
// leaving a spinner up forever. The default covers ordinary calls; the calls
// that wait on Claude (a scan can make two) get a longer one.
export const DEFAULT_TIMEOUT_MS = 90_000;
export const LLM_TIMEOUT_MS = 150_000;
// Overall cap on one streamed analysis / resume parse (server work is
// ~10-30s; a provider retry can stretch it, this is the "give up" line).
export const STREAM_TIMEOUT_MS = 180_000;

export const api = axios.create({
  baseURL: API_URL,
  withCredentials: true, // allow hosted OAuth session cookie
  timeout: DEFAULT_TIMEOUT_MS,
});

api.interceptors.request.use((config) => {
  const t = localStorage.getItem("gumi_token");
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    // Let the caller decide what to do — don't auto-redirect (single-page client)
    return Promise.reject(err);
  }
);

export const uploadResume = (file, { signal } = {}) => {
  const formData = new FormData();
  formData.append("file", file);
  return api.post("/resumes/upload", formData, {
    headers: { "Content-Type": "multipart/form-data" },
    signal,
  });
};

export const parseResume = (resumeId, { force = false } = {}) =>
  api.post(`/resumes/${resumeId}/parse`, null, force ? { params: { force: true } } : undefined);

// ── Server-sent-event POSTs (analysis + resume parse) ──
// fetch()+ReadableStream rather than the native EventSource, which is
// GET-only and can't carry the Authorization header.
//
// runEventStream guarantees the caller hears exactly ONE terminal outcome:
// onComplete(result), onError({ message, code?, status? }) or onCancel().
// That covers every way a stream can end badly: the connection failing, the
// server closing it without a final event, a read error mid-stream, the
// overall timeout, and a non-2xx answer before the stream even starts.
// Callers pass an AbortSignal to cancel; that reports onCancel, not an error.
const CONNECT_MESSAGE = "Cannot connect to server. Please try again.";
const TIMEOUT_MESSAGE =
  "This is taking longer than expected. Please try again, and email support@gumiscore.com if it keeps happening.";
const SESSION_EXPIRED_MESSAGE = "Your session has expired. Please sign in again.";
const RATE_LIMITED_MESSAGE = "You're doing that too quickly. Please wait a few minutes and try again.";

// The message for a non-2xx answer that arrived before any event.
const readHttpErrorMessage = async (response) => {
  if (response.status === 401) return SESSION_EXPIRED_MESSAGE;
  try {
    const detail = (await response.json())?.detail;
    if (typeof detail === "string" && detail.trim()) return detail;
    if (typeof detail?.message === "string" && detail.message.trim()) return detail.message;
  } catch {
    // body wasn't JSON: fall through to the status-based text
  }
  if (response.status === 429) return RATE_LIMITED_MESSAGE;
  return null;
};

const runEventStream = async ({ url, body, signal, timeoutMs, interruptedMessage, onStage, onComplete, onError, onCancel }) => {
  const token = localStorage.getItem("gumi_token");
  const controller = new AbortController();
  let timedOut = false;
  let settled = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);
  const onCallerAbort = () => controller.abort();
  if (signal) {
    if (signal.aborted) controller.abort();
    else signal.addEventListener("abort", onCallerAbort, { once: true });
  }

  const settle = (fn, arg) => {
    if (settled) return;
    settled = true;
    fn?.(arg);
  };
  const fail = (event) => settle(onError, event);
  // The stream was cut short on purpose: by the timeout (an error) or by the caller (not one).
  const stopped = () => (timedOut ? fail({ message: TIMEOUT_MESSAGE }) : settle(onCancel));

  try {
    if (controller.signal.aborted) return stopped(); // cancelled before we started
    let response;
    try {
      response = await fetch(url, {
        method: "POST",
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: body === undefined ? undefined : JSON.stringify(body),
        signal: controller.signal,
      });
    } catch {
      if (controller.signal.aborted) return stopped();
      return fail({ message: CONNECT_MESSAGE });
    }

    if (!response.ok || !response.body) {
      return fail({ message: (await readHttpErrorMessage(response)) || interruptedMessage, status: response.status });
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    try {
      while (!settled) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // SSE events are separated by a blank line; a chunk can hold 0, 1, or
        // several complete events and may end mid-event, so only consume
        // complete "...\n\n" segments.
        let boundary;
        // eslint-disable-next-line no-cond-assign
        while (!settled && (boundary = buffer.indexOf("\n\n")) !== -1) {
          const rawEvent = buffer.slice(0, boundary);
          buffer = buffer.slice(boundary + 2);
          const line = rawEvent.split("\n").find((l) => l.startsWith("data: "));
          if (!line) continue;
          let event;
          try {
            event = JSON.parse(line.slice(6));
          } catch {
            continue; // ignore a malformed/partial event rather than crash the reader loop
          }
          if (event.stage === "complete") settle(onComplete, event.result);
          else if (event.stage === "error") fail(event);
          else onStage?.(event.stage, event.message);
        }
      }
    } catch {
      if (controller.signal.aborted) return stopped();
      return fail({ message: interruptedMessage });
    } finally {
      try {
        reader.cancel?.()?.catch?.(() => {});
      } catch {
        // already closed
      }
    }
    // The server closed the stream without ever sending complete or error.
    if (!settled) fail({ message: interruptedMessage });
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener("abort", onCallerAbort);
  }
};

// SSE progress variant of parseResume: same extraction, but streams staged
// status messages while it runs. onError receives a plain message string.
export const parseResumeWithProgress = (resumeId, { force = false, signal, onStage, onComplete, onError, onCancel } = {}) =>
  runEventStream({
    url: `${API_URL}/resumes/${resumeId}/parse/progress${force ? "?force=true" : ""}`,
    signal,
    timeoutMs: STREAM_TIMEOUT_MS,
    interruptedMessage:
      "Something interrupted the upload. Please try again, and email support@gumiscore.com if it keeps happening.",
    onStage,
    onComplete,
    onCancel,
    onError: (event) =>
      onError?.(
        event.message ||
          "We couldn't read your resume. Try re-uploading it, or email support@gumiscore.com if it keeps failing."
      ),
  });

// Passwordless email OTP — AuthModal's primary sign-in/sign-up flow.
export const requestOtp = (email) => api.post("/auth/otp/request", { email });
export const verifyOtp = (email, code) =>
  api.post("/auth/otp/verify", { email, code, signup_source: getSignupSource() });

export const saveCareerProfile = (payload) => api.post("/career-profiles", payload);

export const getActiveCareerProfile = () => api.get("/career-profiles/active");

// SSE progress variant of POST /analyze: same shared _execute_analyze() on
// the backend, streams staged status messages while it runs. onError receives
// the server's error event ({ message, code }) so callers can branch on code.
export const analyzeWithProgress = (payload, { signal, onStage, onComplete, onError, onCancel } = {}) =>
  runEventStream({
    url: `${API_URL}/analyze/stream`,
    body: payload,
    signal,
    timeoutMs: STREAM_TIMEOUT_MS,
    interruptedMessage:
      "Something interrupted the analysis. Please try again, and email support@gumiscore.com if it keeps happening.",
    onStage,
    onComplete,
    onError,
    onCancel,
  });

export const applyToAnalysis = (analysisId) => api.post(`/analyses/${analysisId}/apply`);

// outcome: "heard_back" | "still_waiting" | "rejected"
export const updateAnalysisOutcome = (analysisId, outcome, detail) =>
  api.post(`/analyses/${analysisId}/outcome/${outcome}`, detail ? { detail } : {});

// ── Phase 2: application tracker + points ──

export const createApplication = ({ analysisId, company, role, jobUrl, fitScore }) =>
  api.post("/applications", {
    analysis_id: analysisId,
    company,
    role,
    job_url: jobUrl,
    fit_score: fitScore,
  });

export const listApplications = (status) => api.get("/applications", { params: status ? { status } : undefined });

// status: applied | heard_back | interviewing | offer | rejected | ghosted | withdrawn | closed
export const updateApplicationStatus = (appId, status, { interviewDate, notes } = {}) =>
  api.patch(`/applications/${appId}/status`, {
    status,
    interview_date: interviewDate,
    notes,
  });

export const updateApplicationOutcome = (appId, outcome) =>
  api.patch(`/applications/${appId}/outcome`, { outcome });

export const withdrawApplication = (appId) => api.delete(`/applications/${appId}`);

export const getMyPoints = () => api.get("/users/me/points");

export const updateMe = (fields) => api.patch("/users/me", fields);

export const updateMyContact = (fields) => api.patch("/users/me/contact", fields);

export const deleteMe = () => api.delete("/users/me");

export const updateMySalary = ({ ctcLpa, variablePercent, stockLpa }) =>
  api.patch("/users/me/salary", {
    ctc_lpa: ctcLpa,
    variable_percent: variablePercent,
    stock_lpa: stockLpa,
  });

export const getDashboard = () => api.get("/dashboard");

// Every analysis the user has run, most recent first, each with its
// `application` tracker row joined in when one exists — powers Dashboard's
// "My Analyses" and Watchlist's "My analyses" segment.
export const listAnalyses = () => api.get("/analyses");

export const deleteAnalysis = (analysisId) => api.delete(`/analyses/${analysisId}`);

// Skills Gap Ledger — aggregated read over the user's own analyses
// (backend: services/skills_ledger.py). {unlocked:false, total_analyses}
// below 5 analyses; {unlocked:true, total_analyses, gaps, strengths} at 5+.
export const getSkillsLedger = () => api.get("/skills-ledger", { timeout: LLM_TIMEOUT_MS });
// Unlock status only — never triggers semantic clustering, so it is safe to call from another page.
export const getSkillsLedgerSummary = () => api.get("/skills-ledger?summary=true");

// ── Companies I'm watching (Watchlist segment 2) ──
export const listCompaniesWatching = () => api.get("/companies-watching");

export const addCompanyWatch = ({ companyName, companyDomain }) =>
  api.post("/companies-watching", { company_name: companyName, company_domain: companyDomain });

export const removeCompanyWatch = (watchId) => api.delete(`/companies-watching/${watchId}`);

// ── ATS Score Checker ──
// resumeId is optional (defaults to the user's active resume server-side);
// jdText is optional — omitting it runs a generic (cacheable) scan, a JD
// runs a fresh targeted scan and adds the Targeting category.
export const runAtsScore = ({ resumeId, jdText } = {}) =>
  api.post(
    "/ats/score",
    {
      resume_id: resumeId || undefined,
      jd_text: jdText || undefined,
    },
    { timeout: LLM_TIMEOUT_MS }
  );
